import { createFilterOptions } from "@mui/material/Autocomplete";
import { z } from "zod";

// The API's limit on a referrer or primary GP email (KIN-2211).
export const CONTACT_EMAIL_MAX_LENGTH = 250;

export const CONTACT_OR_EMAIL_BOTH_MESSAGE =
  "Choose a directory contact or enter an email, not both.";

export const contactIdSchema = z.number().nullable().optional();

// Trimmed here because the API rejects an address with surrounding spaces, and an
// all-spaces value, with a 400.
export const contactEmailSchema = z.preprocess(
  (value) => (typeof value === "string" ? value.trim() : value),
  z
    .literal("")
    .or(
      z
        .string()
        .max(CONTACT_EMAIL_MAX_LENGTH, "Email must be 250 characters or fewer")
        .email("Please enter a valid email address")
    )
    .optional()
);

/**
 * A superRefine step: a link is a contact or an email, never both (the API answers both
 * together with a 400).
 */
export const refineContactOrEmail =
  ({ contactField, emailField }) =>
  (data, ctx) => {
    if (data[contactField] != null && !!data[emailField]?.trim()) {
      ctx.addIssue({
        path: [emailField],
        code: z.ZodIssueCode.custom,
        message: CONTACT_OR_EMAIL_BOTH_MESSAGE,
      });
    }
  };

/**
 * The pair as the API takes it: a numeric contact id or null, a trimmed email or null.
 * Both are always present, because a save that leaves them out clears them.
 */
export const toContactOrEmailPayload = ({ contactId, email }) => {
  const trimmedEmail = typeof email === "string" ? email.trim() : "";
  const numericContactId =
    contactId === null || contactId === undefined || contactId === ""
      ? null
      : Number(contactId);

  return {
    contactId: Number.isFinite(numericContactId) ? numericContactId : null,
    email: trimmedEmail || null,
  };
};

// Names repeat across the directory (many "Boots …", doctors sharing a name), so the
// county is what tells two entries apart.
export const formatContactLocation = (contact) =>
  [contact?.address1, contact?.county].filter(Boolean).join(", ");

export const formatContactLink = ({ contact, email }) => {
  if (contact?.name) {
    return contact.email ? `${contact.name} (${contact.email})` : contact.name;
  }

  return email || "";
};

// Thousands of options: show the first matches only, and search the county and address
// as well as the name.
export const filterContactOptions = createFilterOptions({
  limit: 100,
  stringify: (contact) =>
    [contact.name, contact.county, contact.address1].filter(Boolean).join(" "),
});
